import { Injectable, computed, inject } from '@angular/core';
import { SubscriptionStateService } from './subscription-state.service';
import { PlanFeature } from '../models/model';
import {
  AGENT_FEATURES,
  BOB_V1_FEATURES,
  CONTRACT_ANALYSIS_FEATURES,
  INN_DOC_FEATURES,
  INTEGRATIONS_FEATURES,
  IPlan as IPlanNew
} from '../../pricing/models';
import {
  UI_FEATURE,
  FEATURE_REQUIREMENTS,
  FeatureAvailability,
  FEATURE_GROUPS
} from '../models/feature-mapping';
import { SUBSCRIPTION_STATUS } from '../models/enums';

/**
 * Centralized Feature Flag Service using Angular signals.
 * Provides reactive access to plan features across the application.
 * 
 * New Recommended Usage (with feature mapping):
 * ```typescript
 * constructor(public features: FeatureFlagService) {}
 * 
 * ngOnInit() {
 *   // Check if a UI feature is available
 *   const projectsAvailability = this.features.checkFeature(UI_FEATURE.PROJECTS);
 *   if (projectsAvailability.allowed) {
 *     // Show projects
 *   }
 *   if (projectsAvailability.status.isPaid) {
 *     // Full access
 *   } else if (projectsAvailability.status.isTrialActive) {
 *     // Trial access with limitations
 *   }
 * }
 * ```
 * 
 * Legacy Usage (still supported):
 * ```typescript
 * if (this.features.hasFeature(PlanFeature.AGENT)) {
 *   // Show agent features
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {
  private subscriptionState = inject(SubscriptionStateService);

  /**
   * 🎯 NEW: Check if a UI feature is available based on the feature mapping
   * 
   * This is the recommended way to check features throughout the app.
   * It checks if ANY of the required features for a UI element are present in the plan.
   * 
   * @param uiFeature - The UI feature to check (e.g., UI_FEATURE.PROJECTS)
   * @returns FeatureAvailability object with:
   *   - allowed: boolean (if feature is in plan)
   *   - status: payment/trial status information
   *   - matchingFeatures: list of features that matched
   * 
   * @example
   * const projectsAccess = this.features.checkFeature(UI_FEATURE.PROJECTS);
   * if (projectsAccess.allowed) {
   *   // Show projects menu
   *   if (projectsAccess.status.isPaid) {
   *     // Full access
   *   } else if (projectsAccess.status.isTrialActive) {
   *     // Trial access - show trial badge
   *   }
   * }
   */
  public checkFeature(uiFeature: UI_FEATURE): FeatureAvailability {
    const requiredFeatures = FEATURE_REQUIREMENTS[uiFeature];

    // If no requirements, feature is always available (e.g., EXPLORE)
    if (!requiredFeatures || requiredFeatures.length === 0) {
      return {
        allowed: true,
        status: this.getSubscriptionStatus(),
        matchingFeatures: []
      };
    }

    const plan = this.subscriptionState.plan() as any as IPlanNew;
    let matchingFeatures: string[] = [];
    let allowed = false;

    // ✅ Special handling for BOB_V1 and AGENT to check specific arrays
    if (uiFeature === UI_FEATURE.ASK_BOB) {
      // Check specifically in BOB_V1 array
      if (plan?.features?.BOB_V1 && plan.features.BOB_V1.length > 0) {
        matchingFeatures = requiredFeatures.filter(reqFeature =>
          plan.features.BOB_V1?.includes(reqFeature as any)
        );
        allowed = matchingFeatures.length > 0;
      }
    } else if (uiFeature === UI_FEATURE.GENERAL_AGENT) {
      // Check specifically in AGENT array
      if (plan?.features?.AGENT && plan.features.AGENT.length > 0) {
        matchingFeatures = requiredFeatures.filter(reqFeature =>
          plan.features.AGENT?.includes(reqFeature as any)
        );
        allowed = matchingFeatures.length > 0;
      }
    } else {
      // For all other features, use flattened plan features
      const planFeatures = this.getAllPlanFeatures();
      matchingFeatures = requiredFeatures.filter(reqFeature =>
        planFeatures.includes(reqFeature)
      );
      allowed = matchingFeatures.length > 0;
    }

    return {
      allowed,
      status: this.getSubscriptionStatus(),
      matchingFeatures
    };
  }

  /**
   * Check multiple UI features at once
   * @param uiFeatures - Array of UI features to check
   * @returns Map of UI feature to availability
   */
  public checkMultipleFeatures(uiFeatures: UI_FEATURE[]): Map<UI_FEATURE, FeatureAvailability> {
    const results = new Map<UI_FEATURE, FeatureAvailability>();

    uiFeatures.forEach(feature => {
      results.set(feature, this.checkFeature(feature));
    });

    return results;
  }

  /**
   * Check all features in a group
   * @param groupName - Name of the feature group (e.g., 'SIDEBAR_AGENT')
   * @returns Map of UI feature to availability
   */
  public checkFeatureGroup(groupName: keyof typeof FEATURE_GROUPS): Map<UI_FEATURE, FeatureAvailability> {
    const features = [...FEATURE_GROUPS[groupName]] as UI_FEATURE[];
    return this.checkMultipleFeatures(features);
  }

  /**
   * Get all features present in the user's plan (flattened array)
   * @private
   */
  private getAllPlanFeatures(): string[] {
    const plan = this.subscriptionState.plan() as any as IPlanNew;
    if (!plan?.features) {
      return [];
    }

    const features: string[] = [];

    // Add BOB_V1 features
    if (plan.features.BOB_V1) {
      features.push(...plan.features.BOB_V1);
    }

    // Add AGENT features
    if (plan.features.AGENT) {
      features.push(...plan.features.AGENT);
    }

    // Add INN_DOC features (all sub-categories)
    if (plan.features.INN_DOC) {
      // Only add main-key and its nested features if it has at least one feature
      if (plan.features.INN_DOC.CONTRACT_ANALYSIS?.length) {
        features.push(INN_DOC_FEATURES.CONTRACT_ANALYSIS);
        features.push(...plan.features.INN_DOC.CONTRACT_ANALYSIS);
      }
      if (plan.features.INN_DOC.PROPOSAL_ANALYSIS?.length) {
        features.push(INN_DOC_FEATURES.PROPOSAL_ANALYSIS);
        features.push(...plan.features.INN_DOC.PROPOSAL_ANALYSIS);
      }
      if (plan.features.INN_DOC.SPECIFICATION_ANALYSIS?.length) {
        features.push(INN_DOC_FEATURES.SPECIFICATION_ANALYSIS);
        features.push(...plan.features.INN_DOC.SPECIFICATION_ANALYSIS);
      }
      if (plan.features.INN_DOC.OPPORTUNITY_ANALYSIS?.length) {
        features.push(INN_DOC_FEATURES.OPPORTUNITY_ANALYSIS);
        features.push(...plan.features.INN_DOC.OPPORTUNITY_ANALYSIS);
      }
      if (plan.features.INN_DOC.GEOTECHNICAL_ANALYSIS?.length) {
        features.push(INN_DOC_FEATURES.GEOTECHNICAL_ANALSYIS);
        // features.push(...plan.features.INN_DOC.GEOTECHNICAL_ANALYSIS);
      }
    }

    // Add INTEGRATIONS features
    if (plan.features.INTEGRATIONS) {
      features.push(...plan.features.INTEGRATIONS);
    }

    return features;
  }

  /**
   * Get subscription status information
   * 
   * Delegates to subscription-state.service.ts (single source of truth):
   * - isPaid: uses subscriptionState.hasPaid()
   * - isTrialActive: uses subscriptionState.isTrial()
   * - isExpired: uses subscriptionState.hasSubscriptionEnded()
   * - trialDaysLeft: uses subscriptionState.trialDaysLeft()
   * 
   * @private
   */
  private getSubscriptionStatus(): FeatureAvailability['status'] {
    const subscriptionDetails = this.subscriptionState.subscriptionDetails();
    const subscription = subscriptionDetails?.subscriptions;
    const status = subscription?.status;

    // All checks delegate to subscription-state service (single source of truth)
    const isPaid = this.subscriptionState.hasPaid();
    const trialDaysLeft = this.subscriptionState.trialDaysLeft() || 0;
    const isTrialActive = this.subscriptionState.isTrial();

    return {
      isPaid,
      isTrialActive,
      isExpired: this.subscriptionState.hasSubscriptionEnded(),
      trialDaysLeft,
      subscriptionStatus: status || null
    };
  }

  // ========================================
  // LEGACY METHODS (kept for backward compatibility)
  // ========================================

  /*


  /**
   * Get raw plan features object with all nested features
   */
  public readonly planFeaturesObject = computed(() => {
    const plan = this.subscriptionState.plan() as any as IPlanNew;
    return plan?.features || null;
  });

  // Legacy support - keep old computed properties
  // Computed: Is AGENT feature available
  public readonly hasAgentFeature = computed(() => {
    return this.checkFeature(UI_FEATURE.ASK_BOB).allowed;
  });

  // Computed: Is INN_DOC feature available
  public readonly hasInnDocFeature = computed(() => {
    return this.checkFeature(UI_FEATURE.DOCUMENTS).allowed;
  });

  // Computed: Get raw plan features array
  public readonly rawPlanFeatures = computed(() => {
    const plan = this.subscriptionState.plan() as any as IPlanNew;
    const features = plan?.features;

    // If features is an array, return it
    if (Array.isArray(features)) {
      return features;
    }

    // If features is an object (hierarchical structure), flatten it
    if (features && typeof features === 'object' && !Array.isArray(features)) {
      const flattened: string[] = [];
      // Type assertion: we know features is the hierarchical structure from IPlanNew
      const featuresObj = features as IPlanNew['features'];

      // Handle BOB_V1 array
      if (featuresObj.BOB_V1 && Array.isArray(featuresObj.BOB_V1)) {
        flattened.push(...featuresObj.BOB_V1);
      }

      // Handle AGENT array
      if (featuresObj.AGENT && Array.isArray(featuresObj.AGENT)) {
        flattened.push(...featuresObj.AGENT);
      }

      // Handle INN_DOC object
      if (featuresObj.INN_DOC && typeof featuresObj.INN_DOC === 'object') {
        Object.values(featuresObj.INN_DOC).forEach((value: any) => {
          if (Array.isArray(value)) {
            flattened.push(...value);
          }
        });
      }

      // Handle INTEGRATIONS array
      if (featuresObj.INTEGRATIONS && Array.isArray(featuresObj.INTEGRATIONS)) {
        flattened.push(...featuresObj.INTEGRATIONS);
      }

      // Handle CUSTOMIZED_SOLUTIONS array
      if (featuresObj.CUSTOMIZED_SOLUTIONS && Array.isArray(featuresObj.CUSTOMIZED_SOLUTIONS)) {
        // CUSTOMIZED_SOLUTIONS is an array of objects, extract names or flatten differently
        featuresObj.CUSTOMIZED_SOLUTIONS.forEach((item: any) => {
          if (item.name) {
            flattened.push(item.name);
          }
        });
      }

      return flattened;
    }

    // Default to empty array
    return [];
  });

  /**
   * Check if a specific feature is available
   * @param feature - The feature to check (e.g., PlanFeature.AGENT)
   * @returns boolean indicating if feature is available
   * @deprecated For UI_FEATURE enum values, use checkFeature() instead
   */
  public hasFeature(feature: PlanFeature | string): boolean {
    const featureStr = typeof feature === 'string' ? feature : feature as string;

    switch (featureStr.toUpperCase()) {
      case PlanFeature.AGENT:
      case 'AGENT':
        return this.hasAgentFeature();
      case PlanFeature.INN_DOC:
      case 'INN_DOC':
      case 'INNDOC':
        return this.hasInnDocFeature();
      default:
        // Check raw features for custom features
        const features = this.rawPlanFeatures();
        if (!Array.isArray(features)) {
          return false;
        }
        return features.some(f =>
          f.toUpperCase() === featureStr.toUpperCase()
        );
    }
  }

  /**
   * Check if ALL of the provided features are available
   * @param features - Array of features to check
   * @returns boolean indicating if all features are available
   */
  public hasAllFeatures(features: (PlanFeature | string)[]): boolean {
    return features.every(feature => this.hasFeature(feature));
  }

  /**
   * Check if ANY of the provided features are available
   * @param features - Array of features to check
   * @returns boolean indicating if any feature is available
   */
  public hasAnyFeature(features: (PlanFeature | string)[]): boolean {
    return features.some(feature => this.hasFeature(feature));
  }

  /**
   * Get all available features as an array
   * @returns Array of available feature strings
   */
  public getAvailableFeatures(): string[] {
    const features: string[] = [];

    if (this.hasAgentFeature()) {
      features.push(PlanFeature.AGENT);
    }

    if (this.hasInnDocFeature()) {
      features.push(PlanFeature.INN_DOC);
    }

    return features;
  }

  /**
   * Check if user has access to a specific feature with custom logic
   * @param feature - Feature to check
   * @param customCheck - Optional custom validation function
   * @returns boolean indicating if feature is accessible
   */
  public canAccessFeature(
    feature: PlanFeature | string,
    customCheck?: () => boolean
  ): boolean {
    const hasFeature = this.hasFeature(feature);

    if (!hasFeature) {
      return false;
    }

    if (customCheck) {
      return customCheck();
    }

    return true;
  }

  /**
   * Check if a specific BOB_V1 feature is available
   * @param feature - BOB_V1_FEATURES enum value
   */
  public hasSpecificBobV1Feature(feature: BOB_V1_FEATURES): boolean {
    const plan = this.subscriptionState.plan() as any as IPlanNew;
    return plan?.features?.BOB_V1?.includes(feature) || false;
  }

  /**
   * Check if a specific AGENT feature is available
   * @param feature - AGENT_FEATURES enum value
   */
  public hasSpecificAgentFeature(feature: AGENT_FEATURES): boolean {
    const plan = this.subscriptionState.plan() as any as IPlanNew;
    return plan?.features?.AGENT?.includes(feature) || false;
  }

  /**
   * Check if a specific CONTRACT_ANALYSIS feature is available
   * @param feature - CONTRACT_ANALYSIS_FEATURES enum value
   */
  public hasSpecificContractFeature(feature: CONTRACT_ANALYSIS_FEATURES): boolean {
    const plan = this.subscriptionState.plan() as any as IPlanNew;
    return plan?.features?.INN_DOC?.CONTRACT_ANALYSIS?.includes(feature) || false;
  }

  /**
   * Check if a specific INTEGRATIONS feature is available
   * @param feature - INTEGRATIONS_FEATURES enum value
   */
  public hasSpecificIntegrationFeature(feature: INTEGRATIONS_FEATURES): boolean {
    const plan = this.subscriptionState.plan() as any as IPlanNew;
    return plan?.features?.INTEGRATIONS?.includes(feature) || false;
  }
}
